import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@shared/components/ui/shadcn/skeleton';
import { faCheckCircle, faBox, faArrowTurnLeft, faBolt } from '@fortawesome/pro-light-svg-icons';

interface StatsData {
  delivered: number;
  inTransit: number;
  returns: number;
  other: number;
}

interface StatsProps {
  data: StatsData | null;
  isLoading?: boolean;
  className?: string;
}

export const Stats: React.FC<StatsProps> = ({ data, isLoading = false, className = '' }) => {
  const { t } = useTranslation('ordersShippedModule');

  // Si no hay datos y no está cargando, no mostrar nada
  if (!isLoading && (!data || Object.keys(data).length === 0)) {
    return null;
  }

  const safeData: StatsData = data ?? {
    delivered: 0,
    inTransit: 0,
    returns: 0,
    other: 0,
  };

  {
    /* Los datos ya vienen como porcentajes desde la API - Data already comes as percentages from API */
  }

  {
    /* Configuración de estadísticas con iconos y colores - Stats configuration with icons and colors */
  }
  const stats = [
    {
      title: t('stats.delivered'),
      percentage: safeData.delivered,
      icon: faCheckCircle,
      iconColor: 'text-success',
    },
    {
      title: t('stats.inTransit'),
      percentage: safeData.inTransit,
      icon: faBox,
      iconColor: 'text-info',
    },
    {
      title: t('stats.returns'),
      percentage: safeData.returns,
      icon: faArrowTurnLeft,
      iconColor: 'text-warning',
    },
    {
      title: t('stats.other'),
      percentage: safeData.other,
      icon: faBolt,
      iconColor: 'text-badge-status-2',
    },
  ];

  return (
    <>
      {/* Stats Grid Container - Contenedor de estadísticas */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ${className}`}>
        {stats.map((stat, index) => (
          <div key={index} className="border border-card-border rounded-lg p-4 min-h-[84px]">
            <div className="flex items-center gap-3">
              {/* Stat Icon Container - Contenedor del ícono */}
              <div className={`w-8 h-8 rounded-lg bg-card-bg flex items-center justify-center flex-shrink-0`}>
                {isLoading ? (
                  <Skeleton className="w-3 h-3 rounded-sm" />
                ) : (
                  <FontAwesomeIcon icon={stat.icon} className={`w-3 h-3 ${stat.iconColor}`} />
                )}
              </div>
              {/* Stat Content - Contenido de la estadística */}
              <div className="flex flex-col">
                {isLoading ? (
                  <>
                    <Skeleton className="h-4 w-28 mb-1" />
                    <Skeleton className="h-5 w-16" />
                  </>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-complementary-text mb-1">{stat.title}</h3>
                    <div className="text-base font-semibold text-title">{stat.percentage.toFixed(2)}%</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
